import { isNonEmptyString } from "@/utils/string";

import type { FieldValueType } from "../types";

/**
 * value/labelオブジェクトの型定義
 */
export interface ValueLabelObject extends Record<string, unknown> {
  value: string;
  label: string;
  description?: string;
}

/**
 * 値が{value, label}形式のオブジェクトかどうかを判定する型ガード関数
 *
 * @param value - 判定対象の値
 * @returns value/labelオブジェクトの場合はtrue
 */
export const isValueLabelObject = (
  value: unknown,
): value is ValueLabelObject => {
  if (
    value &&
    typeof value === "object" &&
    value !== null &&
    "value" in value &&
    "label" in value
  ) {
    const obj = value as Record<string, unknown>;

    const valueIsString =
      typeof obj.value === "string" &&
      obj.value !== "[object Object]" &&
      !obj.value.startsWith("[object ");
    const labelIsString =
      typeof obj.label === "string" &&
      obj.label !== "[object Object]" &&
      !obj.label.startsWith("[object ");

    return valueIsString && labelIsString;
  }

  return false;
};

/**
 * value/labelオブジェクトから値部分を抽出
 *
 * @param value - 抽出対象の値
 * @returns 値部分（文字列）
 */
export const extractValueFromValueLabel = (value: FieldValueType): string => {
  if (isValueLabelObject(value)) {
    return value.value;
  }

  if (isNonEmptyString(value)) {
    return value;
  }

  return "";
};

/**
 * value/labelオブジェクトからラベル部分を抽出
 *
 * @param value - 抽出対象の値
 * @returns ラベル部分、存在しない場合は値をそのまま返す
 */
export const extractLabelFromValueLabel = (value: FieldValueType): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (isValueLabelObject(value)) {
    return value.label;
  }

  return String(value);
};

/**
 * フォーム送信用に値を変換する
 * {value, label}オブジェクトの場合のみvalue部分を抽出し、その他の値はそのまま保持
 *
 * @param value - 変換対象の値
 * @returns {value, label}オブジェクトの場合はvalue部分、そうでなければ元の値をそのまま返す
 */
export const extractValueForSubmission = (
  value: FieldValueType,
): FieldValueType => {
  if (isValueLabelObject(value)) {
    return value.value;
  }

  return value;
};

/**
 * 値とオプション配列から{value, label}オブジェクトを作成
 *
 * @param value - 対象の値
 * @param options - 選択肢の配列
 * @returns ラベルが見つかった場合は{value, label}オブジェクト、そうでなければ値のまま
 */
export const createValueLabelObject = (
  value: string,
  options: Array<{
    value: string;
    label: string;
    description?: string | null | undefined;
  }>,
): ValueLabelObject | string => {
  if (!value) return "";

  const selectedOption = options.find((option) => option.value === value);

  if (selectedOption?.label) {
    return {
      value,
      label: selectedOption.label,
      ...(selectedOption.description && {
        description: selectedOption.description,
      }),
    };
  }

  return value;
};

/**
 * 値に対応するラベルを取得
 *
 * @param value - 対象の値
 * @param options - 選択肢の配列
 * @returns 対応するラベル、見つからない場合は値をそのまま返す
 */
export const getSelectedOptionLabel = (
  value: string,
  options: Array<{
    value: string;
    label: string;
    description?: string | null | undefined;
  }>,
): string => {
  if (!value) return "";

  const selectedOption = options.find((option) => option.value === value);
  return selectedOption?.label || value;
};

/**
 * value/labelオブジェクトが持つ情報を使って適切な表示用文字列を生成
 *
 * @param value - 対象の値
 * @param options - 選択肢の配列（オプション）
 * @returns 表示用の文字列
 */
export const getDisplayLabel = (
  value: FieldValueType,
  options?: Array<{ value: string; label: string }>,
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  // value/labelオブジェクトの場合はラベルを優先
  if (isValueLabelObject(value)) {
    return value.label;
  }

  // オブジェクトだが{value, label}形式でない場合のチェック
  // この場合はユーザーにエラーを表示する
  if (typeof value === "object" && value !== null) {
    return "Unknown Object";
  }

  const stringValue = String(value);

  // オプション配列から対応するラベルを検索
  if (options && options.length > 0) {
    const selectedOption = options.find(
      (option) => option.value === stringValue,
    );
    if (selectedOption) {
      return selectedOption.label;
    }
  }

  return stringValue;
};
